import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Plus, MapPin, Sparkles, Check, X, List, CalendarDays, Trash2, ArrowRightLeft, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR } from '@/lib/auth';
import api from '@/lib/api';

const TYPE_COLORS = {
  fixed: 'bg-red-100 text-red-700 hover:bg-red-100',
  half_day: 'bg-orange-100 text-orange-700 hover:bg-orange-100',
};
const TYPE_LABELS = { fixed: 'Fixed', half_day: 'Half Day' };
const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function canManageHolidays() {
  return isAdmin() || isHR();
}

export default function HolidaysPage() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', type: 'fixed', location: 'All' });
  const [flexiAllowance, setFlexiAllowance] = useState(null);
  const [flexiRequests, setFlexiRequests] = useState([]);
  const [flexiOpen, setFlexiOpen] = useState(false);
  const [flexiForm, setFlexiForm] = useState({ date: '', reason: '' });
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapHoliday, setSwapHoliday] = useState(null);
  const [swapDate, setSwapDate] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editHoliday, setEditHoliday] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', date: '', type: 'fixed', location: 'All' });
  const admin = canManageHolidays();

  const load = async () => {
    try {
      const params = admin && locationFilter !== 'all' ? { location: locationFilter } : {};
      const [hRes, fAllRes, fReqRes] = await Promise.all([
        api.get('/api/holidays', { params }),
        api.get('/api/flexi-holidays/allowance'),
        api.get('/api/flexi-holidays/requests'),
      ]);
      setHolidays(hRes.data);
      setFlexiAllowance(fAllRes.data);
      setFlexiRequests(fReqRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [locationFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!form.name || !form.date) { toast.error('Fill all fields'); return; }
    try {
      await api.post('/api/holidays', form);
      toast.success('Holiday added');
      setOpen(false);
      setForm({ name: '', date: '', type: 'fixed', location: 'All' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleFlexiRequest = async () => {
    if (!flexiForm.date || !flexiForm.reason) { toast.error('Fill date and reason'); return; }
    try {
      await api.post('/api/flexi-holidays/request', flexiForm);
      toast.success('Flexi holiday requested');
      setFlexiOpen(false);
      setFlexiForm({ date: '', reason: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleFlexiAction = async (id, action) => {
    try {
      await api.put(`/api/flexi-holidays/requests/${id}/${action}`);
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try { await api.delete(`/api/holidays/${id}`); toast.success('Holiday deleted'); load(); } catch (e) { toast.error('Failed'); }
  };

  const handleSwap = async () => {
    if (!swapDate) { toast.error('Select new date'); return; }
    try {
      await api.post(`/api/holidays/${swapHoliday.id}/swap`, { new_date: swapDate });
      toast.success(`Holiday date swapped to ${swapDate}`);
      setSwapOpen(false); setSwapHoliday(null); setSwapDate('');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/api/holidays/${editHoliday.id}`, editForm);
      toast.success('Holiday updated');
      setEditOpen(false); setEditHoliday(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const openSwapDialog = (h) => { setSwapHoliday(h); setSwapDate(''); setSwapOpen(true); };
  const openEditDialog = (h) => { setEditHoliday(h); setEditForm({ name: h.name, date: h.date, type: h.type, location: h.location || 'All' }); setEditOpen(true); };

  const filtered = filter === 'all' ? holidays : holidays.filter(h => h.type === filter);
  const fixedCount = holidays.filter(h => h.type === 'fixed').length;
  const halfCount = holidays.filter(h => h.type === 'half_day').length;
  const [viewMode, setViewMode] = useState('calendar');

  // Build holiday map for calendar
  const holidayMap = {};
  holidays.forEach(h => { holidayMap[h.date] = h; });

  if (loading) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-5" data-testid="holidays-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// HOLIDAYS</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Holiday Calendar 2026</h1>
          <p className="text-sm text-slate-500">
            {flexiAllowance ? `${flexiAllowance.location} office` : 'Location-specific holidays'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {admin && (
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[160px] h-9" data-testid="location-filter">
                <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400" />
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="Ranchi">Ranchi</SelectItem>
                <SelectItem value="Tatisilwai">Tatisilwai</SelectItem>
                <SelectItem value="Bhubaneswar">Bhubaneswar</SelectItem>
                <SelectItem value="Bihar">Bihar</SelectItem>
                <SelectItem value="West Bengal">West Bengal</SelectItem>
              </SelectContent>
            </Select>
          )}
          {admin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246] gap-1" data-testid="add-holiday-btn"><Plus className="w-4 h-4" />Add Holiday</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-sm">
                <DialogHeader><DialogTitle>Add Holiday</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                  <div><Label>Type</Label>
                    <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="half_day">Half Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="All" /></div>
                  <Button onClick={handleAdd} className="w-full bg-[#0D2850] hover:bg-[#0B2246]">Add</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter(filter === 'fixed' ? 'all' : 'fixed')}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold font-tabular text-red-600" data-testid="fixed-count">{fixedCount}</p>
            <p className="text-[10px] text-slate-500 uppercase font-medium">Fixed Holidays</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter(filter === 'half_day' ? 'all' : 'half_day')}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold font-tabular text-orange-500" data-testid="half-day-count">{halfCount}</p>
            <p className="text-[10px] text-slate-500 uppercase font-medium">Half-Day</p>
          </CardContent>
        </Card>
        <Card
          className={`border-slate-200 transition-shadow ${!admin ? 'cursor-pointer hover:shadow-md' : ''}`}
          onClick={() => {
            if (!flexiAllowance || flexiAllowance.remaining <= 0) { toast.info('No flexi holidays remaining for your location'); return; }
            setFlexiOpen(true);
          }}
          data-testid="flexi-available-card"
        >
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold font-tabular text-purple-600" data-testid="flexi-count">
              {flexiAllowance ? `${flexiAllowance.remaining}/${flexiAllowance.allowed}` : '-'}
            </p>
            <p className="text-[10px] text-slate-500 uppercase font-medium">Flexi Available</p>
            <p className="text-[10px] text-purple-500 mt-0.5">Click to request</p>
          </CardContent>
        </Card>
      </div>

      {/* Holiday View - Calendar + Table */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList className="bg-slate-100 mb-3" data-testid="holiday-view-tabs">
          <TabsTrigger value="calendar" className="gap-1"><CalendarDays className="w-3.5 h-3.5" />Calendar</TabsTrigger>
          <TabsTrigger value="list" className="gap-1"><List className="w-3.5 h-3.5" />List</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <HolidayCalendarGrid holidays={holidays} holidayMap={holidayMap} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Holiday</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    {admin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((h, i) => {
                    const d = new Date(h.date + 'T00:00:00');
                    const dayName = DAY_NAMES[d.getDay()];
                    const past = d < new Date(new Date().toDateString());
                    return (
                      <TableRow key={h.id || i} className={past ? 'opacity-50' : ''} data-testid={`holiday-row-${i}`}>
                        <TableCell className="text-xs text-slate-400">{i + 1}</TableCell>
                        <TableCell className="text-sm font-medium">{h.name}</TableCell>
                        <TableCell className="text-sm font-tabular">{h.date}</TableCell>
                        <TableCell className={`text-sm ${dayName === 'Sunday' ? 'text-red-500' : ''}`}>{dayName}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${TYPE_COLORS[h.type] || 'bg-slate-100 text-slate-600'}`}>
                            {TYPE_LABELS[h.type] || h.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{h.location || 'All'}
                        </TableCell>
                        {admin && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600" onClick={() => openEditDialog(h)} title="Edit" data-testid={`edit-holiday-${h.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-600" onClick={() => openSwapDialog(h)} title="Swap Date" data-testid={`swap-holiday-${h.id}`}>
                                <ArrowRightLeft className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(h.id)} title="Delete" data-testid={`delete-holiday-${h.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Flexi Holidays Section */}
      <Card className="border-slate-200" data-testid="flexi-holidays-section">
        <CardHeader className="py-3 px-4 pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" /> Flexi Holidays
            {flexiAllowance && (
              <Badge className="bg-purple-100 text-purple-700 text-[10px] hover:bg-purple-100 ml-1">
                {flexiAllowance.remaining} of {flexiAllowance.allowed} remaining
              </Badge>
            )}
          </CardTitle>
          {flexiAllowance && flexiAllowance.remaining > 0 && (
            <Dialog open={flexiOpen} onOpenChange={setFlexiOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1 text-purple-700 border-purple-200 hover:bg-purple-50" data-testid="request-flexi-btn">
                  <Plus className="w-3.5 h-3.5" />Request Flexi
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-sm">
                <DialogHeader><DialogTitle>Request Flexi Holiday</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Date</Label><Input type="date" value={flexiForm.date} onChange={e => setFlexiForm({...flexiForm, date: e.target.value})} data-testid="flexi-date-input" /></div>
                  <div><Label>Festival / Reason</Label><Textarea value={flexiForm.reason} onChange={e => setFlexiForm({...flexiForm, reason: e.target.value})} placeholder="e.g., Celebrating Diwali with family" data-testid="flexi-reason-input" /></div>
                  <Button onClick={handleFlexiRequest} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="flexi-submit-btn">Submit Request</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <p className="text-xs text-slate-500 mb-3">
            Flexi holidays are extra paid days off for festivals you celebrate. Request a date and get admin approval.
          </p>
          {flexiRequests.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">No flexi holiday requests yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {admin && <TableHead>Employee</TableHead>}
                  <TableHead>Date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  {admin && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {flexiRequests.map((r) => (
                  <TableRow key={r.id} data-testid={`flexi-request-${r.id}`}>
                    {admin && <TableCell className="text-sm">{r.employee_name} <span className="text-[10px] text-slate-400">({r.location})</span></TableCell>}
                    <TableCell className="text-sm font-tabular">{r.date}</TableCell>
                    <TableCell className="text-sm">{r.reason}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${
                        r.status === 'approved' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' :
                        r.status === 'rejected' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                        'bg-amber-100 text-amber-700 hover:bg-amber-100'
                      }`}>{r.status}</Badge>
                    </TableCell>
                    {admin && (
                      <TableCell>
                        {r.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50" onClick={() => handleFlexiAction(r.id, 'approve')} data-testid={`approve-flexi-${r.id}`}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleFlexiAction(r.id, 'reject')} data-testid={`reject-flexi-${r.id}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Swap Holiday Dialog */}
      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Swap Holiday Date</DialogTitle></DialogHeader>
          {swapHoliday && (
            <div className="space-y-3 mt-2">
              <p className="text-sm"><strong>{swapHoliday.name}</strong> — current date: <Badge className="text-xs">{swapHoliday.date}</Badge></p>
              <div><Label>New Date</Label><Input type="date" value={swapDate} onChange={e => setSwapDate(e.target.value)} data-testid="swap-date-input" /></div>
              <Button onClick={handleSwap} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="swap-confirm-btn">Swap Date</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Holiday Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Edit Holiday</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-holiday-name" /></div>
            <div><Label>Date</Label><Input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} data-testid="edit-holiday-date" /></div>
            <div><Label>Type</Label>
              <Select value={editForm.type} onValueChange={v => setEditForm({...editForm, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input value={editForm.location} onChange={e => setEditForm({...editForm, location: e.target.value})} data-testid="edit-holiday-location" /></div>
            <Button onClick={handleEdit} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="edit-holiday-submit">Update</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function HolidayCalendarGrid({ holidays, holidayMap }) {
  const MONTHS_2026 = Array.from({ length: 12 }, (_, i) => i);
  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="holiday-calendar-grid">
      {MONTHS_2026.map(m => {
        const year = 2026;
        const daysInMonth = new Date(year, m + 1, 0).getDate();
        const firstDay = new Date(year, m, 1).getDay();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);

        return (
          <div key={m} className="border border-slate-200 rounded-lg p-3">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">{MONTH_NAMES[m]}</h3>
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {DOW.map((d, i) => <div key={i} className={`text-[10px] font-medium py-0.5 ${i === 0 ? 'text-red-400' : 'text-slate-400'}`}>{d}</div>)}
              {cells.map((day, i) => {
                if (!day) return <div key={`e${i}`} className="h-7" />;
                const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const hol = holidayMap[dateStr];
                const isSunday = new Date(year, m, day).getDay() === 0;
                const isToday = dateStr === todayStr;
                let bg = '';
                let textColor = isSunday ? 'text-red-500' : 'text-slate-700';
                if (hol) {
                  bg = hol.type === 'fixed' ? 'bg-red-100' : 'bg-orange-100';
                  textColor = hol.type === 'fixed' ? 'text-red-700' : 'text-orange-700';
                }
                if (isToday) bg = bg || 'bg-[#0D2850]';

                return (
                  <div key={day} className={`h-7 w-7 mx-auto rounded-full flex items-center justify-center text-[11px] font-medium relative cursor-default ${bg} ${isToday && !hol ? 'text-white' : textColor}`}
                    title={hol ? `${hol.name} (${hol.type === 'fixed' ? 'Full Day' : 'Half Day'})` : ''}>
                    {day}
                    {hol && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-current" />}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4 flex gap-4 items-center justify-center text-xs text-slate-500 pt-2">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-100 border border-red-200" /> Fixed Holiday</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-100 border border-orange-200" /> Half Day</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#0D2850]" /> Today</span>
        <span className="flex items-center gap-1"><span className="text-red-500 font-medium">S</span> Sunday</span>
      </div>
    </div>
  );
}
